import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    DefaultButton,
    Dialog,
    DialogType,
    FontIcon,
    getTheme,
    useTheme,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TooltipHost
} from '@fluentui/react';
import AutocompleteSearchBox, {ISuggestionItem} from '../Common/AutocompleteSearchBox';
import {PILL_STYLES} from '../Common/SuggestionPill';
import {useLazyGetExportExtensionOptionsQuery, useLazyGetExportExtensionSuggestionsQuery} from '../../store/Api';
import {useLazyGetWorkspaceRecordLinksQuery} from '../../store/endpoints/RecordLinksEndpoints';
import {IExtensionOption} from '../../models/Extension';
import OpportunityBrowserDialog from './OpportunityBrowserDialog';

interface IOpportunityPickerDialogProps {
    /** The opportunity direct-import (sub-)extension whose PSA the search runs against. */
    extensionId: string;
    onDismiss: () => void;
    /** Called with the chosen opportunity's id; the caller drives the quick-flow from there. */
    onPick: (opportunityId: string) => void;
}

/** How many suggested-opportunity tags to show under the search box. */
const SUGGESTION_COUNT = 5;
/** How many opportunities the dropdown holds once a company is scoped. */
const COMPANY_DROPDOWN_COUNT = 25;
/** How many company matches to mix into the typed-search results before the opportunity matches. */
const COMPANY_MATCH_COUNT = 3;
/** The sub-extension's hidden PSA-wide opportunity search field (also serves the suggestions). */
const OPPORTUNITY_SEARCH_FIELD = 'opportunitySearch';
/** The sub-extension's company field, whose options search the PSA's active companies. */
const COMPANY_FIELD = 'company';

// Neutrals only: getTheme() is Fluent's default palette (App applies the brand theme via ThemeProvider),
// so anything brand-colored below reads useTheme() inside a component instead.
const theme = getTheme();
const SUGGESTION_ICON: React.CSSProperties = {marginRight: 8, color: theme.palette.neutralSecondary, fontSize: 12};

/** A company the user picked from the typed search; scopes both the search and the suggested tags. */
interface ICompanyChip {
    id: string;
    name: string;
}

const formatTotal = (value: unknown): string | undefined => {
    if (typeof value !== 'number') return undefined;
    try {
        return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(value);
    } catch {
        return `$${value}`;
    }
};

class OpportunitySuggestion implements ISuggestionItem {
    constructor(private key: string, private text: string, private option?: IExtensionOption, private importedWorkspace?: string) {}
    getSearchText: () => string = () => this.text;
    getKey: () => string = () => this.key;
    getSuggestionItem() {
        const extra = this.option?.additionalProperties ?? {};
        const details = [
            formatTotal(extra.total),
            extra.status ? String(extra.status) : undefined,
            extra.salesRep ? `Sales rep: ${String(extra.salesRep)}` : undefined,
            describeAge(extra.lastUpdated ? String(extra.lastUpdated) : undefined),
            this.importedWorkspace ? 'Already imported. Opens its workspace.' : undefined
        ].filter((d): d is string => !!d);
        return (
            <div key={this.key} className='suggestionItem'>
                <FontIcon iconName={this.importedWorkspace ? 'OpenFolderHorizontal' : 'Trending12'} style={SUGGESTION_ICON} />
                {this.text}
                {details.length > 0 &&
                    <div style={{marginLeft: 20, color: theme.palette.neutralSecondary, fontSize: 12}}>
                        {details.join(' · ')}
                    </div>}
            </div>
        );
    }
}

/** The dropdown's closing row when a company is scoped: opens the full paged/sortable browser. */
class MoreOpportunitiesSuggestion implements ISuggestionItem {
    constructor(public readonly onOpen: () => void) {}
    getSearchText: () => string = () => '';
    getKey: () => string = () => 'more-opportunities';
    getSuggestionItem() {
        return <MoreOpportunitiesRow key={this.getKey()} />;
    }
}

const MoreOpportunitiesRow: React.FC = () => {
    const brand = useTheme();
    return (
        <div className='suggestionItem' style={{color: brand.palette.themePrimary}}>
            <FontIcon iconName='More' style={SUGGESTION_ICON} />
            More…
        </div>
    );
};

class CompanySuggestion implements ISuggestionItem {
    constructor(public readonly id: string, public readonly name: string) {}
    // Picking a company turns it into a filter chip rather than search text, so the box clears.
    getSearchText: () => string = () => '';
    getKey: () => string = () => `company:${this.id}`;
    getSuggestionItem() {
        return (
            <div key={this.getKey()} className='suggestionItem'>
                <FontIcon iconName='CityNext2' style={SUGGESTION_ICON} />
                {this.name}
                <span style={{marginLeft: 8, color: theme.palette.neutralSecondary, fontSize: 12}}>
                    Company. Filters the opportunity search.
                </span>
            </div>
        );
    }
}


export const describeAge = (iso?: string): string | undefined => {
    if (!iso) return undefined;
    const ms = Date.now() - new Date(iso).getTime();
    if (isNaN(ms)) return undefined;
    const days = Math.floor(ms / 86400000);
    if (days <= 0) return 'Updated today';
    if (days === 1) return 'Updated yesterday';
    if (days < 30) return `Updated ${days} days ago`;
    const months = Math.floor(days / 30);
    return months < 12 ? `Updated ${months} month${months === 1 ? '' : 's'} ago` : 'Updated over a year ago';
};

interface ISuggestedTagProps {
    option: IExtensionOption;
    /** Set when this opportunity was already imported: picking it opens that workspace instead. */
    workspaceId?: string;
    onPick: () => void;
}

const SuggestedTag: React.FC<ISuggestedTagProps> = ({option, workspaceId, onPick}) => {
    const brand = useTheme();
    const extra = option.additionalProperties ?? {};
    const mine = extra.mine === true;
    const details = [
        extra.company ? String(extra.company) : undefined,
        extra.salesRep ? `Sales rep: ${String(extra.salesRep)}` : undefined,
        describeAge(extra.lastUpdated ? String(extra.lastUpdated) : undefined),
        mine ? 'Assigned to you' : undefined,
        workspaceId ? 'Already imported. Opens its workspace.' : undefined
    ].filter((d): d is string => !!d);
    return (
        <TooltipHost content={<div>{option.display}{details.map((d) => <div key={d}>{d}</div>)}</div>}>
            <DefaultButton
                text={option.display}
                iconProps={{iconName: workspaceId ? 'OpenFolderHorizontal' : mine ? 'Contact' : 'Trending12'}}
                styles={mine ? {...PILL_STYLES, root: {...(PILL_STYLES.root as object), borderColor: brand.palette.themePrimary}} : PILL_STYLES}
                ariaLabel={`Create prepared cart from ${option.display}`}
                onClick={onPick}
            />
        </TooltipHost>
    );
};

/**
 * The palette's "Create prepared cart from opportunity" picker. Behaves like the omnibox: typing matches
 * both the PSA's companies and its open opportunities; picking a company becomes a chip that scopes the
 * opportunity search (and the suggested tags) to that customer. Below the box, up to five suggested
 * opportunities (the user's own recent open ones first, via the extension's suggestions endpoint) are
 * offered as tags so the common case is a single click. Either way the pick hands off to the quote-link
 * quick flow, which builds the workspace and prepared cart automatically.
 */
const OpportunityPickerDialog: React.FC<IOpportunityPickerDialogProps> = (props) => {
    const [getExtensionOptions] = useLazyGetExportExtensionOptionsQuery();
    const [getSuggestions] = useLazyGetExportExtensionSuggestionsQuery();
    const [getRecordLinks] = useLazyGetWorkspaceRecordLinksQuery();
    const [inProgress, setInProgress] = useState(false);
    const [suggestions, setSuggestions] = useState<ISuggestionItem[]>();
    const [searchError, setSearchError] = useState<string>();
    const [company, setCompany] = useState<ICompanyChip>();
    const [suggested, setSuggested] = useState<IExtensionOption[]>();
    const [suggestedLoading, setSuggestedLoading] = useState(true);
    /** Opportunity id → workspace id, for suggestions that were already imported. */
    const [imported, setImported] = useState<Record<string, string>>({});
    const [browseOpen, setBrowseOpen] = useState(false);
    // Guards against a slow earlier search overwriting a newer one's results.
    const searchSequence = useRef(0);
    /** The company-scoped dropdown's resting content (25 recent opportunities + the More row). */
    const baselineRef = useRef<ISuggestionItem[]>();

    const companyScope = company ? {[COMPANY_FIELD]: company.id} : undefined;

    const toOpportunitySuggestions = (options: IExtensionOption[], links: Record<string, string>, chip?: ICompanyChip) => {
        const items: ISuggestionItem[] = options.map((o) => new OpportunitySuggestion(o.value, o.display, o, links[o.value]));
        if (chip) items.push(new MoreOpportunitiesSuggestion(() => setBrowseOpen(true)));
        return items;
    };

    // Suggestions load on open and reload whenever the company chip changes. Without a chip they only
    // feed the tag strip; with one, the same (larger) query also becomes the dropdown's resting content.
    useEffect(() => {
        let cancelled = false;
        setSuggestedLoading(true);
        baselineRef.current = undefined;
        (async () => {
            try {
                const result = await getSuggestions({
                    extensionId: props.extensionId,
                    field: OPPORTUNITY_SEARCH_FIELD,
                    limit: company ? COMPANY_DROPDOWN_COUNT : SUGGESTION_COUNT,
                    additionalData: companyScope
                }).unwrap();
                if (cancelled) return;
                // An extension without suggestions support answers success=false; that just means no tags.
                const options = result?.success === false ? [] : (result?.options ?? []);
                setSuggested(options.slice(0, SUGGESTION_COUNT));
                const links = options.length > 0
                    ? await getRecordLinks({field: 'opportunityId', externalIds: options.map((o) => o.value)})
                        .unwrap().catch(() => [])
                    : [];
                if (cancelled) return;
                const linkMap = Object.fromEntries(links.map((l) => [l.externalId, l.workspaceId]));
                setImported(linkMap);
                if (company) {
                    baselineRef.current = toOpportunitySuggestions(options, linkMap, company);
                    setSuggestions(baselineRef.current);
                }
            } catch {
                if (!cancelled) setSuggested([]);
            } finally {
                if (!cancelled) setSuggestedLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [company?.id, props.extensionId]);

    const search = useCallback(async (text?: string) => {
        const term = (text ?? '').trim();
        if (term.length < 2) {
            // A scoped box falls back to its resting 25 rather than closing the dropdown.
            setSuggestions(company ? baselineRef.current : undefined);
            return;
        }
        const sequence = ++searchSequence.current;
        setInProgress(true);
        try {
            if (company) {
                // Scoped search goes through the suggestions endpoint so rows keep their details.
                const result = await getSuggestions({
                    extensionId: props.extensionId,
                    field: OPPORTUNITY_SEARCH_FIELD,
                    search: term,
                    limit: COMPANY_DROPDOWN_COUNT,
                    additionalData: companyScope
                }).unwrap();
                if (sequence !== searchSequence.current) return;
                setSearchError(result?.success === false ? (result.error || 'The extension failed to search') : undefined);
                const options = result?.success === false ? [] : (result?.options ?? []);
                const links = options.length > 0
                    ? await getRecordLinks({field: 'opportunityId', externalIds: options.map((o) => o.value)})
                        .unwrap().catch(() => [])
                    : [];
                if (sequence !== searchSequence.current) return;
                setSuggestions(toOpportunitySuggestions(options,
                    Object.fromEntries(links.map((l) => [l.externalId, l.workspaceId])), company));
                return;
            }
            const [opportunities, companies] = await Promise.all([
                getExtensionOptions({
                    extensionId: props.extensionId,
                    field: OPPORTUNITY_SEARCH_FIELD,
                    search: term
                }).unwrap(),
                getExtensionOptions({extensionId: props.extensionId, field: COMPANY_FIELD, search: term})
                    .unwrap().catch(() => undefined)
            ]);
            if (sequence !== searchSequence.current) return;
            setSearchError(opportunities?.success === false ? (opportunities.error || 'The extension failed to search') : undefined);
            setSuggestions([
                ...(companies?.options ?? []).slice(0, COMPANY_MATCH_COUNT).map((o) => new CompanySuggestion(o.value, o.display)),
                ...(opportunities?.options ?? []).map((o) => new OpportunitySuggestion(o.value, o.display))
            ]);
        } catch {
            if (sequence === searchSequence.current) setSearchError('The extension failed to search');
        } finally {
            if (sequence === searchSequence.current) setInProgress(false);
        }
    }, [company?.id, props.extensionId]);

    const onSuggestionClicked = (suggestion: string | ISuggestionItem) => {
        if (typeof suggestion === 'string') return;
        if (suggestion instanceof CompanySuggestion) {
            setSuggestions(undefined);
            setSearchError(undefined);
            setCompany({id: suggestion.id, name: suggestion.name});
            return;
        }
        if (suggestion instanceof MoreOpportunitiesSuggestion) {
            suggestion.onOpen();
            return;
        }
        props.onPick(suggestion.getKey());
    };

    return (
        <Dialog
            hidden={false}
            onDismiss={props.onDismiss}
            minWidth={520}
            dialogContentProps={{
                type: DialogType.normal,
                title: 'Create Prepared Cart from Opportunity',
                subText: 'Pick an opportunity. The workspace and prepared cart are built automatically.'
            }}>
            {company &&
                <Stack horizontal verticalAlign='center' tokens={{childrenGap: 8}} styles={{root: {marginBottom: 8}}}>
                    <Text variant='small' style={{color: theme.palette.neutralSecondary}}>Company</Text>
                    <TooltipHost content='Remove the company filter'>
                        <DefaultButton
                            text={company.name}
                            iconProps={{iconName: 'CityNext2'}}
                            menuIconProps={{iconName: 'Cancel'}}
                            styles={PILL_STYLES}
                            ariaLabel={`Remove company filter ${company.name}`}
                            onClick={() => {
                                setSuggestions(undefined);
                                setCompany(undefined);
                            }}
                        />
                    </TooltipHost>
                </Stack>}
            <AutocompleteSearchBox
                placeholder={company ? `Search ${company.name} opportunities` : 'Search opportunities or companies'}
                onChange={async (_, value) => await search(value)}
                onSuggestionClicked={onSuggestionClicked}
                suggestions={suggestions}
                inProgress={inProgress}
                debounceTime={500}
                showSuggestionsOnFocus={!!company}
                layerSuggestions={true}
            />
            {searchError && <MessageBar messageBarType={MessageBarType.error}>{searchError}</MessageBar>}
            <div style={{marginTop: 12}}>
                <Text variant='small' style={{color: theme.palette.neutralSecondary}}>
                    {company ? `Recent open opportunities for ${company.name}` : 'Suggested'}
                </Text>
                <Stack horizontal wrap verticalAlign='center' tokens={{childrenGap: 6}} styles={{root: {marginTop: 6, minHeight: 28}}}>
                    {suggestedLoading &&
                        <Spinner size={SpinnerSize.xSmall} label='Finding recent opportunities' labelPosition='right' />}
                    {!suggestedLoading && (suggested?.length ?? 0) === 0 &&
                        <Text variant='small' style={{color: theme.palette.neutralTertiary}}>
                            No recent open opportunities{company ? ' for this company' : ''}.
                        </Text>}
                    {!suggestedLoading && suggested?.map((option) =>
                        <SuggestedTag
                            key={option.value}
                            option={option}
                            workspaceId={imported[option.value]}
                            onPick={() => props.onPick(option.value)}
                        />)}
                </Stack>
            </div>
            {browseOpen && company &&
                <OpportunityBrowserDialog
                    extensionId={props.extensionId}
                    companyId={company.id}
                    companyName={company.name}
                    onDismiss={() => setBrowseOpen(false)}
                    onPick={props.onPick}
                />}
        </Dialog>
    );
};

export default OpportunityPickerDialog;
